<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import whatsappChannel from 'dashboard/api/channel/whatsappChannel';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { runEmbeddedSignup } = useWhatsappEmbeddedSignup();
    return { v$: useVuelidate(), runEmbeddedSignup };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      mmApiEnabled: false,
      forwardingWebhookUrl: '',
      callingEnabled: false,
      isLoadingCallingSettings: false,
      isUpdatingCallingSettings: false,
      webhookVerificationToken: '',
      isGeneratingWebhookVerificationToken: false,
      isSettingDefaults: false,
      isReconfiguring: false,
      isRegisteringTwilioWebhooks: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    showWhatsAppReconfigure() {
      return (
        this.isEmbeddedSignupWhatsApp &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          this.isOnChatwootCloud
            ? FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP_FLOW
            : FEATURE_FLAGS.WHATSAPP_RECONFIGURE
        )
      );
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    isWhatsAppAPIKeyConfigured() {
      return this.inbox.provider_config?.api_key_configured === true;
    },
    isWebhookVerificationTokenConfigured() {
      return (
        this.inbox.provider_config?.webhook_verify_token_configured === true
      );
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
      this.fetchCallingSettings();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  async mounted() {
    this.setDefaults();
    await this.fetchCallingSettings();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.mmApiEnabled = this.inbox.provider_config?.mm_api_enabled || false;
      this.forwardingWebhookUrl =
        this.inbox.provider_config?.forwarding_webhook_url || '';
      this.callingEnabled = this.inbox.calling_enabled === true;
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    async fetchCallingSettings() {
      if (
        !this.isAWhatsAppChannel ||
        this.isATwilioChannel ||
        this.isAZuckZapGoChannel
      ) {
        return;
      }

      this.isLoadingCallingSettings = true;
      try {
        const response = await this.$store.dispatch(
          'inboxes/getCallingSettings',
          this.inbox.id
        );
        // Meta API returns calling_enabled as a boolean property
        const callingEnabled = response?.settings?.calling_enabled;
        if (typeof callingEnabled === 'boolean') {
          this.callingEnabled = callingEnabled;
        }
      } catch (error) {
        this.callingEnabled = this.inbox.calling_enabled === true;
      } finally {
        this.isLoadingCallingSettings = false;
      }
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async generateWebhookVerificationToken() {
      this.webhookVerificationToken = '';
      this.isGeneratingWebhookVerificationToken = true;
      try {
        const { data } = await InboxesAPI.generateWhatsAppWebhookVerifyToken(
          this.inbox.id
        );
        this.webhookVerificationToken = data.webhook_verify_token || '';
        if (!this.webhookVerificationToken) {
          throw new Error('Missing webhook verification token');
        }
      } catch {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.TOKEN_ERROR'));
      } finally {
        this.isGeneratingWebhookVerificationToken = false;
      }
    },
    async updateForwardingWebhookUrl() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              forwarding_webhook_url: this.forwardingWebhookUrl,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async registerTwilioWebhooks() {
      this.isRegisteringTwilioWebhooks = true;
      try {
        await InboxesAPI.registerTwilioWebhooks(this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_REGISTER_WEBHOOKS_SUCCESS')
        );
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          this.$t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_REGISTER_WEBHOOKS_ERROR');
        useAlert(message);
      } finally {
        this.isRegisteringTwilioWebhooks = false;
      }
    },
    async reconfigureWhatsApp() {
      this.isReconfiguring = true;
      try {
        const credentials = await this.runEmbeddedSignup();
        // User dismissed the Meta popup without completing signup.
        if (!credentials) return;

        await whatsappChannel.reauthorizeWhatsApp({
          inboxId: this.inbox.id,
          ...credentials,
        });
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_SUCCESS')
        );
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_ERROR')
        );
      } finally {
        this.isReconfiguring = false;
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
    async updateMmApiSetting() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              mm_api_enabled: this.mmApiEnabled,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.mmApiEnabled
            ? this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_ENABLED')
            : this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_DISABLED')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateCallingSetting() {
      const previousValue = !this.callingEnabled;
      this.isUpdatingCallingSettings = true;
      try {
        await this.$store.dispatch('inboxes/updateCallingSettings', {
          id: this.inbox.id,
          calling_enabled: this.callingEnabled,
        });
        useAlert(
          this.callingEnabled
            ? this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_ENABLED')
            : this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_DISABLED')
        );
      } catch (error) {
        this.callingEnabled = previousValue;
        useAlert(this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_ERROR'));
      } finally {
        this.isUpdatingCallingSettings = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="inbox.twilio_delivery_status_webhook_url"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_STATUS_CALLBACK_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_STATUS_CALLBACK_SUBHEADER')
      "
    >
      <woot-code
        :script="inbox.twilio_delivery_status_webhook_url"
        lang="html"
      />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_REGISTER_WEBHOOKS_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_REGISTER_WEBHOOKS_SUBHEADER')
      "
    >
      <NextButton
        :is-loading="isRegisteringTwilioWebhooks"
        @click="registerTwilioWebhooks"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_REGISTER_WEBHOOKS_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_FORWARDING_WEBHOOK_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.TWILIO_FORWARDING_WEBHOOK_SUBHEADER')
      "
    >
      <div
        class="flex flex-1 justify-between items-center whatsapp-settings--content"
      >
        <woot-input
          v-model="forwardingWebhookUrl"
          type="url"
          class="flex-1 mr-2 [&>input]:!mb-0"
          :placeholder="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FORWARDING_WEBHOOK_PLACEHOLDER'
            )
          "
        />
        <NextButton @click="updateForwardingWebhookUrl">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
        </NextButton>
      </div>
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <woot-code :script="inbox.hmac_token" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://docs.airys.chat/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsFieldSection>
        <SettingsFieldSection
          v-if="showWhatsAppReconfigure"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')
          "
        >
          <NextButton
            :is-loading="isReconfiguring"
            :disabled="isReconfiguring"
            @click="reconfigureWhatsApp"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
          </NextButton>
        </SettingsFieldSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else-if="!isEmbeddedSignupWhatsApp">
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <div class="flex flex-col gap-3 items-start">
            <span
              class="inline-flex rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
            >
              {{
                isWebhookVerificationTokenConfigured
                  ? $t('INBOX_MGMT.SETTINGS_POPUP.SECRET_CONFIGURED')
                  : $t('INBOX_MGMT.SETTINGS_POPUP.SECRET_NOT_CONFIGURED')
              }}
            </span>
            <p class="mb-0 text-sm text-n-slate-9">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.TOKEN_REVEAL_NOTICE')
              }}
            </p>
            <NextButton
              :is-loading="isGeneratingWebhookVerificationToken"
              @click="generateWebhookVerificationToken"
            >
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.GENERATE_TOKEN') }}
            </NextButton>
            <woot-code
              v-if="webhookVerificationToken"
              class="w-full"
              :script="webhookVerificationToken"
            />
          </div>
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <span
            class="inline-flex rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
          >
            {{
              isWhatsAppAPIKeyConfigured
                ? $t('INBOX_MGMT.SETTINGS_POPUP.SECRET_CONFIGURED')
                : $t('INBOX_MGMT.SETTINGS_POPUP.SECRET_NOT_CONFIGURED')
            }}
          </span>
        </SettingsFieldSection>
        <SettingsFieldSection
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :help-text="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsFieldSection>
      </template>
      <SettingsFieldSection
        v-if="!isAZuckZapGoChannel"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </SettingsFieldSection>
      <SettingsFieldSection
        v-if="!isAZuckZapGoChannel"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_TITLE')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_SUBHEADER')"
      >
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 items-center">
            <input
              id="callingEnabled"
              v-model="callingEnabled"
              type="checkbox"
              :disabled="isLoadingCallingSettings || isUpdatingCallingSettings"
              @change="updateCallingSetting"
            />
            <label for="callingEnabled" class="text-body-main text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_LABEL') }}
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CALLING_NOTE') }}
          </p>
        </div>
      </SettingsFieldSection>
      <SettingsFieldSection
        v-if="!isAZuckZapGoChannel"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_TITLE')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_SUBHEADER')"
      >
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 items-center">
            <input
              id="mmApiEnabled"
              v-model="mmApiEnabled"
              type="checkbox"
              @change="updateMmApiSetting"
            />
            <label for="mmApiEnabled" class="text-body-main text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_LABEL') }}
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MM_API_NOTE') }}
          </p>
        </div>
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FORWARDING_WEBHOOK_TITLE')
        "
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FORWARDING_WEBHOOK_SUBHEADER')
        "
      >
        <div
          class="flex flex-1 justify-between items-center whatsapp-settings--content"
        >
          <woot-input
            v-model="forwardingWebhookUrl"
            type="url"
            class="flex-1 mr-2 [&>input]:!mb-0"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FORWARDING_WEBHOOK_PLACEHOLDER'
              )
            "
          />
          <NextButton @click="updateForwardingWebhookUrl">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </NextButton>
        </div>
      </SettingsFieldSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  :deep(input) {
    margin-bottom: 0;
  }
}
</style>
